import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ReactSortable } from 'react-sortablejs';
import SidebarLayout from 'components/Layout/Layout';
import { API_URL } from 'env';
import { Modal, Select, Collapse, Card, Row, Col, Input, Button, Switch } from 'antd';
import ImageUpload from 'components/GbCpn/ImageUpload';
import { SaveOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import './AddProduct.scss';
import { Document, Page } from 'react-pdf';

interface Product {
  id: number;
  title: string;
  subtitle: string;
  point_price: number;
  locale: string;
  image: string;
  description: string;
}

const EditProduct: React.FC = () => {
  const navigate = useNavigate();
  const [imageBase64, setImageBase64] = useState('');
  const [pdfFileBase64, setPdfFileBase64] = useState('');
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfUrls, setPdfUrls] = useState<{ vi?: string; en?: string }>({});
  const [serverImageUrls, setServerImageUrls] = useState<{ vi?: string; en?: string }>({});
  const [bannerImageBase64, setBannerImageBase64] = useState('');
  const { Option } = Select;
  const [bonusIds, setBonusIds] = useState<number[]>([]);
  const [bonuses, setBonuses] = useState<Product[]>([]);
  const [bonusLoading, setBonusLoading] = useState(false);

  const indications = [
    "life_path",
    "life_purpose",
    "balance",
    "soul",
    "personality",
    "birth_day",
    "subconscious_strength",
    "maturity",
    "missing_aspects",
    "passion",
    "emotional_response_style",
    "link_connection",
    "milestone_1",
    "milestone_2",
    "milestone_3",
    "milestone_4",
    "challenge_1",
    "challenge_2",
    "challenge_3",
    "challenge_4",
    "soul_personality_link",
    "rational_thinking",
    "age_milestones_1",
    "age_milestones_2",
    "age_milestones_3",
    "age_milestones_4",
    "personal_year",
    "personal_month",
    "personal_day"
  ];

  const [locale, setLocale] = useState<string>('en')

  const [form, setForm] = useState({
    title: {
      en: '',
      vi: '',
    },
    subtitle: {
      en: '',
      vi: '',
    },
    point_price: 0,
    type: {
      en: '',
      vi: '',
    },
    numbers: 0 || {},
    description: {
      en: '',
      vi: '',
    },
    indication: 'life_path',
    key: '',
    is_featured: false,
    recommendation_id: null,
  });

  const [descriptions, setDescriptions] = useState([] as any);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const { id } = useParams<{ id: string }>();
  const [listRcm, setListRcm] = useState([])
  const [rcmLoading, setRcmLoading] = useState(false)
  const rcmSearchTimeout = useRef<any>(null)
  const [imageBase64Vi, setImageBase64Vi] = useState('');
  const [imageBase64En, setImageBase64En] = useState('');
  const [pdfFileBase64En, setPdfFileBase64En] = useState('');
  const [pdfFileBase64Vi, setPdfFileBase64Vi] = useState('');

  const fetchBonuses = async () => {
    try {
      setBonusLoading(true);
      const response = await fetch(`${API_URL}api/v1/be/products`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const json = await response.json();

      if (json.status && json.data && json.data.items) {
        const productData = Array.isArray(json.data.items) ? json.data.items : [];
        setBonuses(productData);
      } else {
        console.error('Error fetching bonuses:', json.message);
        Modal.error({
          title: 'Lỗi',
          content: json.message || 'Không thể tải danh sách products',
        });
      }
    } catch (error: any) {
      setBonuses([]);
      Modal.error({
        title: 'Lỗi kết nối',
        content: 'Không thể kết nối đến server để lấy danh sách products',
      });
    } finally {
      setBonusLoading(false);
    }
  };

  useEffect(() => {
    const getRcm = async () => {
      try {
        setRcmLoading(true)
        const res = await fetch(`${API_URL}api/v1/be/recommendations`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        })
        if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`)
        const json = await res.json();
        if (json.status) {
          setListRcm(json.data.data || [])
        } else {
          setListRcm([])
        }
      } catch (e) {
        setListRcm([])
      } finally {
        setRcmLoading(false)
      }
    }

    getRcm()
  }, [])

  const fetchRecommendations = async (title: string) => {
    try {
      setRcmLoading(true)
      const url = `${API_URL}api/v1/be/recommendations${title ? `?title=${encodeURIComponent(title)}` : ''}`
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      })
      if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`)
      const json = await res.json();
      if (json.status) {
        setListRcm(json.data.data || [])
      } else {
        setListRcm([])
      }
    } catch (e) {
      setListRcm([])
    } finally {
      setRcmLoading(false)
    }
  }

  const handleSearchRcm = (value: string) => {
    if (rcmSearchTimeout.current) clearTimeout(rcmSearchTimeout.current)
    rcmSearchTimeout.current = setTimeout(() => {
      fetchRecommendations(value || '')
    }, 300)
  }

  useEffect(() => {
    fetchBonuses();
  }, []);

  // Update displayed pdfUrl when locale changes
  useEffect(() => {
    if (pdfUrls && (pdfUrls.en || pdfUrls.vi)) {
      setPdfUrl((pdfUrls as any)[locale] || '');
    }
  }, [locale, pdfUrls]);

  const removeDescription = (id: string) => {
    setDescriptions(descriptions.filter((desc: any) => desc.id !== id));
  };

  const safeParse = (str: string) => {
    try {
      return JSON.parse(str);
    } catch {
      return null;
    }
  };

  // Ensure image string is a data URL; if it's an http(s) URL, fetch and convert to base64 data URL
  const ensureImageDataUrlAsync = async (val: any): Promise<string> => {
    if (!val) return '';
    if (typeof val !== 'string') return '';
    if (val.startsWith('data:image/')) return val;
    if (val.startsWith('data:')) return val; // already some data url
    if (val.startsWith('http')) {
      try {
        const res = await fetch(val);
        const blob = await res.blob();
        return await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = () => resolve('');
          reader.readAsDataURL(blob);
        });
      } catch (e) {
        return '';
      }
    }
    // assume raw base64
    return `data:image/png;base64,${val}`;
  };

  const handleSave = async () => {
    if (locale === 'en') {
      if (!form.title.en.trim()) {
        Modal.warning({ title: 'Thiếu thông tin', content: 'Vui lòng nhập tiêu đề' });
        return;
      }
      if (!form.subtitle.en.trim()) {
        Modal.warning({ title: 'Thiếu thông tin', content: 'Vui lòng nhập subtitle' });
        return;
      }

      try {
        setLoading(true);
        // build image payload only with base64 data URLs; convert URLs to base64
        const [imgVi, imgEn] = await Promise.all([
          ensureImageDataUrlAsync(imageBase64Vi || serverImageUrls.vi || ''),
          ensureImageDataUrlAsync(imageBase64En || serverImageUrls.en || ''),
        ]);
        const imagePayload: any = {
          vi: imgVi || imageBase64Vi || serverImageUrls.vi || '',
          en: imgEn || imageBase64En || serverImageUrls.en || '',
        };

        // build pdf_file payload only with base64 data URLs; ensure prefix
        const ensurePdfDataUrl = (val: string) => {
          if (!val) return '';
          if (val.startsWith('data:application/pdf;base64,')) return val;
          if (val.startsWith('data:')) return val; // already some data url
          return `data:application/pdf;base64,${val}`;
        };
        const pdfPayload: any = {};
        if (pdfFileBase64Vi && pdfFileBase64Vi.startsWith('data:')) pdfPayload.vi = ensurePdfDataUrl(pdfFileBase64Vi);
        if (pdfFileBase64En && pdfFileBase64En.startsWith('data:')) pdfPayload.en = ensurePdfDataUrl(pdfFileBase64En);

        const payload = {
          ...form,
          description: descriptions,
          introduction: [
            {
              description: form.description,
            }
          ],
          image: imagePayload,
          ...(Object.keys(pdfPayload).length ? { pdf_file: pdfPayload } : {}),
          numbers: { [form.indication]: Number(form.numbers) },
          bonus_ids: bonusIds,
          is_featured: form.is_featured || false,
        };

        const response = await fetch(`${API_URL}api/v1/be/products/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

        const json = await response.json();

        if (json.status) {
          Modal.success({
            title: 'Success',
            content: 'Operation completed successfully',
            onOk: () => navigate('/product'),
          });
        } else {
          Modal.error({
            title: 'Error',
            content: json.error[Object.keys(json.error)[0]] || 'Unknown error',
          });
        }
      } catch (error: any) {
        Modal.error({
          title: 'Error',
          content: error.message,
        });
      } finally {
        setLoading(false);
      }
    } else if (locale === 'vi') {
      if (!form.title.vi.trim()) {
        Modal.warning({ title: 'Thiếu thông tin', content: 'Vui lòng nhập tiêu đề' });
        return;
      }
      if (!form.subtitle.vi.trim()) {
        Modal.warning({ title: 'Thiếu thông tin', content: 'Vui lòng nhập subtitle' });
        return;
      }

      try {
        setLoading(true);
        // build image payload only with base64 data URLs; convert URLs to base64
        const [imgVi, imgEn] = await Promise.all([
          ensureImageDataUrlAsync(imageBase64Vi || serverImageUrls.vi || ''),
          ensureImageDataUrlAsync(imageBase64En || serverImageUrls.en || ''),
        ]);
        const imagePayload: any = {
          vi: imgVi || imageBase64Vi || serverImageUrls.vi || '',
          en: imgEn || imageBase64En || serverImageUrls.en || '',
        };

        // build pdf_file payload only with base64 data URLs; ensure prefix
        const ensurePdfDataUrl = (val: string) => {
          if (!val) return '';
          if (val.startsWith('data:application/pdf;base64,')) return val;
          if (val.startsWith('data:')) return val; // already some data url
          return `data:application/pdf;base64,${val}`;
        };
        const pdfPayload: any = {};
        if (pdfFileBase64Vi && pdfFileBase64Vi.startsWith('data:')) pdfPayload.vi = ensurePdfDataUrl(pdfFileBase64Vi);
        if (pdfFileBase64En && pdfFileBase64En.startsWith('data:')) pdfPayload.en = ensurePdfDataUrl(pdfFileBase64En);

        const payload = {
          ...form,
          description: descriptions,
          introduction: [
            {
              description: form.description,
            }
          ],
          image: imagePayload,
          ...(Object.keys(pdfPayload).length ? { pdf_file: pdfPayload } : {}),
          numbers: { [form.indication]: Number(form.numbers) },
          bonus_ids: bonusIds,
          is_featured: form.is_featured || false,
        };

        const response = await fetch(`${API_URL}api/v1/be/products/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

        const json = await response.json();

        if (json.status) {
          Modal.success({
            title: 'Success',
            content: 'Operation completed successfully',
            onOk: () => navigate('/product'),
          });
        } else {
          Modal.error({
            title: 'Error',
            content: json.error[Object.keys(json.error)[0]] || 'Unknown error',
          });
        }
      } catch (error: any) {
        Modal.error({
          title: 'Error',
          content: error.message,
        });
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingData(true);
        const response = await fetch(`${API_URL}api/v1/be/products/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        const json = await response.json();

        if (json.status) {
          const data = json.data;

          // set pdf urls for both locales if available
          if (data.pdf_urls && (data.pdf_urls.en || data.pdf_urls.vi)) {
            setPdfUrls(data.pdf_urls);
            setPdfUrl(data.pdf_urls[locale as 'en' | 'vi'] || '');
          } else if (data.pdf_url) {
            // legacy single url
            setPdfUrl(data.pdf_url);
          } else {
            setPdfUrls({});
            setPdfUrl('');
          }

          // Parse các field JSON string
          let titleParse, subtitleParse, descriptionParse, avatarUrl, introductionParse

          try {
            const titleParsed = JSON.parse(json.data.title);
            if (titleParsed && typeof titleParsed === "object" && ("en" in titleParsed || "vi" in titleParsed)) {
              titleParse = titleParsed;
            } else {
              titleParse = { en: json.data.title, vi: '' };
            }
          } catch {
            titleParse = { en: json.data.title, vi: '' };
          }

          // image_url may be object {vi,en} or string
          try {
            const imgField = json.data.image_url;
            if (imgField && typeof imgField === 'object') {
              const viUrl = imgField.vi || '';
              const enUrl = imgField.en || '';
              setImageBase64Vi(viUrl);
              setImageBase64En(enUrl);
              setServerImageUrls({ vi: viUrl, en: enUrl });
            } else if (typeof imgField === 'string') {
              setImageBase64Vi(imgField);
              setImageBase64En(imgField);
              setServerImageUrls({ vi: imgField, en: imgField });
            } else {
              setImageBase64Vi('');
              setImageBase64En('');
              setServerImageUrls({});
            }
          } catch {
            setImageBase64Vi('')
            setImageBase64En('')
            setServerImageUrls({});
          }

          // pdf_file per locale
          try {
            const pdfFileVi = json.data.pdf_file?.vi;
            const pdfFileEn = json.data.pdf_file?.en;
            if (pdfFileVi || pdfFileEn) {
              setPdfFileBase64Vi(pdfFileVi || '');
              setPdfFileBase64En(pdfFileEn || '');
            } else if (json.data.pdf_file) {
              setPdfFileBase64Vi(json.data.pdf_file);
              setPdfFileBase64En(json.data.pdf_file);
            } else {
              setPdfFileBase64Vi('');
              setPdfFileBase64En('');
            }
          } catch {
            setPdfFileBase64Vi('');
            setPdfFileBase64En('');
          }

          try {
            const desParsed = JSON.parse(json.data.description);
            if (desParsed && typeof desParsed === "object" && ("en" in desParsed || "vi" in desParsed)) {
              descriptionParse = desParsed;
            } else {
              descriptionParse = { en: json.data.description, vi: '' };
            }
          } catch {
            descriptionParse = { en: json.data.description, vi: '' };
          }

          try {
            const SubTitParsed = JSON.parse(json.data.subtitle);
            if (SubTitParsed && typeof SubTitParsed === "object" && ("en" in SubTitParsed || "vi" in SubTitParsed)) {
              subtitleParse = SubTitParsed;
            } else {
              subtitleParse = { en: json.data.subtitle, vi: '' };
            }
          } catch {
            subtitleParse = { en: json.data.subtitle, vi: '' };
          }

          try {
            const introParsed = JSON.parse(json.data.introduction);
            if (introParsed && typeof introParsed === "object" && ("en" in introParsed || "vi" in introParsed)) {
              introductionParse = introParsed;
            } else {
              introductionParse = { en: json.data.introduction, vi: '' };
            }
          } catch {
            introductionParse = { en: json.data.introduction, vi: '' };
          }

          // numbers
          const key = Object.keys(data.numbers || {})[0] || "";
          const value = Object.values(data.numbers || {})[0] || 0;

          // ✅ setForm chỉ 1 lần duy nhất
          setForm({
            title: titleParse,
            subtitle: subtitleParse,
            description: descriptionParse,
            numbers: value,
            indication: key,
            point_price: data.point_price || 0,
            is_featured: !!data.is_featured,
            recommendation_id: data.recommendation_id
          } as any);

          // các state phụ
          setLocale(data.locale || "en");
          setBonusIds(data.bonuses?.map((b: any) => b.id) || []);
          setImageBase64(data.image_url || "");
          setPdfFileBase64(data.pdf_file || "");
          setBannerImageBase64(data.banner_image || "");
        } else {
          Modal.error({
            title: "Lỗi",
            content: "Lấy dữ liệu thất bại",
            onOk: () => navigate("/product"),
          });
        }
      } catch (error: any) {
        Modal.error({
          title: "Lỗi",
          content: "Lỗi khi tải dữ liệu: " + error.message,
          onOk: () => navigate("/product"),
        });
      } finally {
        setLoadingData(false);
      }
    };

    fetchData();
  }, [id, navigate]);
  return (
    <SidebarLayout title={<h4 className="text-2xl font-bold !m-0 py-4"></h4>}>
      <div className='head-form-box mb-4'>
        <div className='box-left'>
          <ArrowLeftOutlined
            onClick={() => navigate('/product')}
          />
          <div className='title'>Update Product</div>
        </div>
        <Button loading={loading} onClick={handleSave} type="primary" icon={<SaveOutlined />} size="large">
          Update Product
        </Button>
      </div>
      <Row gutter={[16, 16]} className='AddProduct'>
        <Col span={24} lg={18}>
          <Card>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <div>
                  <label className="block font-semibold mb-1" style={{ color: '#5e5873' }}>
                    Title
                  </label>
                  <Input
                    type="text"
                    value={locale === 'en' ? form.title.en : form.title.vi}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        title: {
                          ...form.title,
                          [locale]: e.target.value,
                        },
                      })
                    }
                  />
                </div>
              </Col>
              <Col span={12}>
                <div>
                  <label className="block font-semibold mb-1" style={{ color: '#5e5873' }}>
                    Subtitle
                  </label>
                  <Input
                    type="text"
                    value={locale === 'en' ? form.subtitle.en : form.subtitle.vi}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        subtitle: {
                          ...form.subtitle,
                          [locale]: e.target.value,
                        },
                      })
                    }
                  />
                </div>
              </Col>
              <Col span={12}>
                <div>
                  <label className="block font-semibold mb-1" style={{ color: '#5e5873' }}>
                    Pwi Indices
                  </label>
                  <Select
                    value={form.indication}
                    onChange={(value) => setForm({ ...form, indication: value })}
                    style={{ width: '100%' }}
                  >
                    {/* <Option value="">-- Chọn key --</Option> */}
                    {indications.map((_, i) => (
                      <Option key={i + 1} value={_}>{_}</Option>
                    ))}
                    {/* {[11, 22, 33].map(num => (
                                    <Option key={num} value={num}>{num}</Option>
                                  ))} */}
                  </Select>
                </div>
              </Col>
              <Col span={12}>
                <div>
                  <label className="block font-semibold mb-1" style={{ color: '#5e5873' }}>
                    Numbers
                  </label>
                  <Select
                    value={form.numbers}
                    onChange={(value) => setForm({ ...form, numbers: value })}
                    style={{ width: '100%' }}
                  >
                    <Option value="">-- Chọn số --</Option>
                    {[...Array(9)].map((_, i) => (
                      <Option key={i + 1} value={i + 1}>{i + 1}</Option>
                    ))}
                    {[11, 22, 33].map(num => (
                      <Option key={num} value={num}>{num}</Option>
                    ))}
                  </Select>
                </div>
              </Col>

              <Col span={12}>
                <div>
                  <label className="block font-semibold mb-1" style={{ color: '#5e5873' }}>
                    Point Price
                  </label>
                  <Input
                    type="number"
                    value={form.point_price}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        point_price: Number(e.target.value),
                      })
                    }
                  />
                </div>
              </Col>
              <Col span={24}>
                <div>
                  <label className="block font-semibold mb-1" style={{ color: '#5e5873' }}>
                    Products (Bonus)
                  </label>
                  <Select
                    mode="multiple"
                    placeholder="Chọn products làm bonus"
                    value={bonusIds}
                    onChange={(value) => setBonusIds(value)}
                    style={{ width: '100%' }}
                    loading={bonusLoading}
                    notFoundContent={bonusLoading ? 'Đang tải...' : 'Không có dữ liệu'}
                  >
                    {Array.isArray(bonuses) && bonuses.map(bonus => (
                      <Option key={bonus.id} value={bonus.id}>
                        {bonus.title}
                      </Option>
                    ))}
                  </Select>
                </div>
              </Col>
              <Col span={24}>
                <div className=''>
                  <label className="block font-semibold mb-1" style={{ color: '#5e5873' }}>
                    Upload Report File
                  </label>
                  <Input
                    type="file"
                    className='hover:cursor-pointer'
                    accept=".pdf"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          const base64String = (reader.result as string).split(',')[1]; // Remove data URL prefix
                          setPdfFileBase64(base64String); // Store base64 content
                          setPdfUrl(`data:application/pdf;base64,${base64String}`);
                          if (locale === 'vi') {
                            setPdfFileBase64Vi('data:application/pdf;base64,' + base64String);
                          } else {
                            setPdfFileBase64En('data:application/pdf;base64,' + base64String);
                          }
                        };
                        reader.readAsDataURL(file);
                      } else {
                        setPdfFileBase64('');
                        setPdfFileBase64Vi('');
                        setPdfFileBase64En('');
                      }
                    }}
                    style={{ width: '100%' }}
                  />
                  {/* {pdfUrl && (
                    <div className="mt-2">
                      <label className="block font-semibold mb-1" style={{ color: '#5e5873' }}>
                        Current PDF Content (Base64)
                      </label>
                      <Input.TextArea
                        value={pdfUrl}
                        readOnly
                        rows={4}
                        style={{ width: '100%', resize: 'none' }}
                      />
                    </div>
                  )} */}
                  {pdfUrl && (
                    <iframe
                      src={pdfUrl}
                      width="100%"
                      height="600px"
                    />
                  )}
                </div>
              </Col>
              <Col span={24}>
                <div className=''>
                  <label className="block font-semibold mb-1" style={{ color: '#5e5873' }}>
                    Select recommendation id
                  </label>
                  <Select
                    showSearch
                    onSearch={handleSearchRcm}
                    filterOption={false}
                    loading={rcmLoading}
                    className='hover:cursor-pointer'
                    style={{ width: '100%' }}
                    onChange={(value) =>
                      setForm({
                        ...form,
                        recommendation_id: value,
                      })
                    }
                    value={form.recommendation_id}
                  >
                    {listRcm && listRcm.map((item: any) => (
                      <Option key={item.id} value={item.id}>
                        {(() => {
                          const parsed = safeParse(item.title)
                          if (parsed) {
                            return locale === 'en' ? parsed.en : parsed.vi
                          }
                          return item.title
                        })()}
                      </Option>
                    ))}
                  </Select>
                </div>
              </Col>
              {/* <Col span={24}>
                <div>
                  <label className="block font-semibold mb-1" style={{ color: '#5e5873' }}>
                    Description
                  </label>
                  <TinyMce
                    defaultValue={introduction.description}
                    onChange={(value: any) => setForm({ ...form, description: value })}
                  />
                </div>
              </Col> */}
            </Row>
          </Card>

          {/* <div className='mt-4'></div>

          <Card title="List content">
            <div className='ctrbox'>
              <div className='box-left'>
                <Button onClick={() => setModalContent({ ...modalContent, show: true, data: { id: null, title: '', content: 'reset', image: 'reset', position: 'Left', hasButton: false } })} icon={<FileAddOutlined />}>Add content</Button>
              </div>
            </div>

            <div className='list-content-product'>
              <ReactSortable
                list={descriptions}
                setList={setDescriptions}
                animation={150}
                className="sortable-list"
                handle=".drag-handle"
              >
                {descriptions.map((desc: any) => (
                  <Collapse key={desc.id} className="mb-2">
                    <Panel
                      header={
                        <div className="flex items-center justify-between">
                          <span>{desc.title || 'New Content'}</span>
                          <div className='box-right-cp'>
                            <div className='ctr del-mod' onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              removeDescription(desc.id)
                            }}>
                              <DeleteOutlined />
                            </div>
                            <div className='ctr edit-mod' onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              setModalContent({ ...modalContent, show: true, data: { ...desc } })
                            }}>
                              <EditOutlined />
                            </div>
                            <div className='ctr drag-handle'>
                              <DragOutlined />
                            </div>
                          </div>
                        </div>
                      }
                      key={desc.id}
                    >
                      <div className='content-des'>
                        <div className={`pos-content ${desc.position || 'Left'}`}>
                          <div className='stt-image'>
                            <img src={desc.image} alt="" />
                          </div>
                          <div className='true-content'>
                            <div dangerouslySetInnerHTML={{ __html: desc.content }} />
                          </div>
                        </div>

                      </div>
                    </Panel>
                  </Collapse>
                ))}
              </ReactSortable>
            </div>

            <Modal
              title={modalContent.data.id ? 'Edit Content' : 'Add Content'}
              closable={{ 'aria-label': 'Custom Close Button' }}
              open={modalContent.show}
              width={1000}
              okText={modalContent.data.id ? 'Update' : 'Add'}
              onOk={() => {
                if (modalContent.data.id) {
                  const updatedDescriptions = descriptions.map((desc: any) =>
                    desc.id === modalContent.data.id ? modalContent.data : desc
                  );
                  setDescriptions(updatedDescriptions);
                } else {
                  descriptions.push({
                    id: modalContent.data.id || Date.now().toString(),
                    title: modalContent.data.title,
                    content: modalContent.data.content,
                    image: modalContent.data.image,
                    position: modalContent.data.position,
                    hasButton: modalContent.data.hasButton || false
                  });
                  setDescriptions([...descriptions]);
                }
                setModalContent({ ...modalContent, show: false });
              }}
              onCancel={() => setModalContent({ ...modalContent, show: false })}
            >
              <Row gutter={[16, 16]} className='modal-content-product'>
                <Col span={12}>
                  <label className="block font-semibold mb-1" style={{ color: '#5e5873' }}>
                    Title
                  </label>
                  <Input
                    placeholder="Title"
                    value={modalContent.data.title}
                    onChange={(e) => setModalContent({
                      ...modalContent,
                      data: { ...modalContent.data, title: e.target.value }
                    })}
                  />
                </Col>
                <Col span={6}>
                  <label className="block font-semibold mb-1" style={{ color: '#5e5873' }}>
                    Position image
                  </label>
                  <Select
                    placeholder="Position"
                    value={modalContent.data.position}
                    onChange={(value) => setModalContent({
                      ...modalContent,
                      data: { ...modalContent.data, position: value }
                    })}
                    style={{ width: '100%' }}
                  >
                    <Option value="Left">Left</Option>
                    <Option value="Right">Right</Option>
                    <Option value="Bottom">Bottom</Option>
                    <Option value="Top">Top</Option>
                    <Option value="NoImage">No image</Option>
                  </Select>
                </Col>
                <Col span={6}>
                  <label className="block font-semibold mb-1" style={{ color: '#5e5873' }}>
                    Has Button
                  </label>
                  <Switch checked={modalContent.data.hasButton} onChange={(value) => setModalContent({ ...modalContent, data: { ...modalContent.data, hasButton: value } })} />
                </Col>
                <Col span={24}>
                  <div className={`pos-content ${modalContent.data.position || 'Left'}`}>
                    <div className='stt-image'>
                      <label className="block font-semibold mb-1" style={{ color: '#5e5873' }}>
                        Image
                      </label>
                      <ImageUpload
                        raito={'380/267'}
                        defaultValue={modalContent.data.image}
                        onChange={(file) => {
                          if (file) {
                            const reader = new FileReader();
                            reader.onloadend = () => setModalContent({
                              ...modalContent,
                              data: { ...modalContent.data, image: reader.result as string }
                            });
                            reader.readAsDataURL(file);
                          } else {
                            setBannerImageBase64('');
                          }
                        }}
                      />
                    </div>
                    <div className='true-content'>
                      <label className="block font-semibold mb-1" style={{ color: '#5e5873' }}>
                        Description
                      </label>
                      <TinyMce
                        defaultValue={modalContent.data.content}
                        onChange={(content: any) => setModalContent({
                          ...modalContent,
                          data: { ...modalContent.data, content: content }
                        })}
                      />
                    </div>
                  </div>
                </Col>
              </Row>
            </Modal>
          </Card> */}
        </Col>
        <Col span={24} lg={6}>
          <Card>
            <div className="mb-4">
              <label className="block font-semibold mb-1" style={{ color: '#5e5873' }}>
                Locale
              </label>
              <Select
                value={locale}
                onChange={(e) => setLocale(e)}
                style={{ width: '100%' }}
              >
                <option value="vi">Vietnam</option>
                <option value="en">English</option>
              </Select>
            </div>

            <div className="mb-4">
              <label className="block font-semibold mb-1" style={{ color: '#5e5873' }}>
                Featured
              </label>
              <Switch checked={form.is_featured} onChange={(value) => setForm({ ...form, is_featured: value })} />
            </div>

            <div className="mb-4">
              <label className="block font-semibold mb-1" style={{ color: '#5e5873' }}>
                Avatar
              </label>
              <div className="relative border-2 border-dashed rounded border-gray-300">
                {locale === 'en' && imageBase64En ? <img src={imageBase64En} alt="" className='absolute top-0 left-0 w-full h-full object-cover object-center' /> : locale === 'en' && <div className='absolute top-1/2 left-1/2 cts-trans-50'>
                  <svg className='w-12 h-12 text-gray-500' xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" ><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M15 8h.01" /><path d="M12.5 21h-6.5a3 3 0 0 1 -3 -3v-12a3 3 0 0 1 3 -3h12a3 3 0 0 1 3 3v6.5" /><path d="M3 16l5 -5c.928 -.893 2.072 -.893 3 0l4 4" /><path d="M14 14l1 -1c.67 -.644 1.45 -.824 2.182 -.54" /><path d="M16 19h6" /><path d="M19 16v6" /></svg>
                </div>}
                {locale === 'vi' && imageBase64Vi ? <img src={imageBase64Vi} alt="" className='absolute top-0 left-0 w-full h-full object-cover object-center' /> : locale === 'vi' && <div className='absolute top-1/2 left-1/2 cts-trans-50'>
                  <svg className='w-12 h-12 text-gray-500' xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" ><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M15 8h.01" /><path d="M12.5 21h-6.5a3 3 0 0 1 -3 -3v-12a3 3 0 0 1 3 -3h12a3 3 0 0 1 3 3v6.5" /><path d="M3 16l5 -5c.928 -.893 2.072 -.893 3 0l4 4" /><path d="M14 14l1 -1c.67 -.644 1.45 -.824 2.182 -.54" /><path d="M16 19h6" /><path d="M19 16v6" /></svg>
                </div>}
                <ImageUpload
                  isHide
                  raito={'267/380'}
                  onChange={(file) => {
                    if (file) {
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        if (locale === "vi") {
                          setImageBase64Vi(reader.result as string);
                        } else if (locale === "en") {
                          setImageBase64En(reader.result as string);
                        }
                      };
                      reader.readAsDataURL(file);
                    } else {
                      if (locale === "vi") {
                        setImageBase64Vi("");
                      } else if (locale === "en") {
                        setImageBase64En("");
                      }
                    }
                  }}
                />
              </div>
            </div>

            {/* <div>
              <label className="block font-semibold mb-1" style={{ color: '#5e5873' }}>
                Banner Image
              </label>
              <ImageUpload
                defaultValue={bannerImageBase64}
                raito={'380/267'}
                onChange={(file) => {
                  if (file) {
                    const reader = new FileReader();
                    reader.onloadend = () => setBannerImageBase64(reader.result as string);
                    reader.readAsDataURL(file);
                  } else {
                    setBannerImageBase64('');
                  }
                }}
              />
            </div> */}
          </Card>
        </Col>
      </Row>
      <div className='ctrbox-foot mt-4'>
        <Button loading={loading} onClick={handleSave} type="primary" icon={<SaveOutlined />} size="large">
          Update Product
        </Button>
      </div>
    </SidebarLayout>
  );
};

export default EditProduct;